/**
 * Attack objects of the bosses (GDD §9.2, issue #95). `shape` is data of the core: the render (#96) picks the geometry
 * from it (disc against elongated bullet) and the audio (#100) a different sound, so a coin and a lead are told apart
 * by form and by sound, never by colour alone (GDD §13.6).
 */
export type BossObjectType = 'silver-coin' | 'lead' | 'plush-hippo' | 'horseshoe' | 'portrait-wall' | 'chess-piece' | 'check-column' | 'barrel';
export type BossObjectShape = 'disc' | 'bullet' | 'plush' | 'horseshoe' | 'portrait' | 'piece' | 'column' | 'barrel';

export interface BossObjectDef {
  readonly shape: BossObjectShape;
  /** Collectable (a circle of `pickupRadius` against the plane, GDD §7) or damaging (an AABB). */
  readonly kind: 'pickup' | 'damaging' | 'wall' | 'piece' | 'check';
  /** Pickups: radius of the circle (u). */
  readonly pickupRadius?: number;
  /** Damaging: AABB of the object (u). */
  readonly hitbox?: { readonly width: number; readonly height: number };
  /** Points of a pickup (GDD §12). */
  readonly points?: number;
  /** Elongation of the model (height over width): the bullet is at least twice as elongated as the disc. */
  readonly aspect: number;
}

export const BOSS_OBJECTS: Readonly<Record<BossObjectType, BossObjectDef>> = Object.freeze({
  'silver-coin': { shape: 'disc', kind: 'pickup', pickupRadius: 0.6, points: 100, aspect: 1 },
  lead: { shape: 'bullet', kind: 'damaging', hitbox: { width: 0.2, height: 0.5 }, aspect: 2.5 },
  'plush-hippo': { shape: 'plush', kind: 'damaging', hitbox: { width: 1, height: 0.7 }, aspect: 1.4 },
  // JEFE-2 (#110): the horseshoe is a small harmful projectile; a portrait wall is two boxes around a gap (see JEFE_2).
  horseshoe: { shape: 'horseshoe', kind: 'damaging', hitbox: { width: 0.7, height: 0.7 }, aspect: 1 },
  'portrait-wall': { shape: 'portrait', kind: 'wall', aspect: 1 },
  // JEFE-3 (#121): a chess piece is a one-cell column that stays where it is (it does not vanish when it touches the plane);
  // the check columns of phase 3 are two boxes around a gap that closes (see JEFE_3).
  'chess-piece': { shape: 'piece', kind: 'piece', hitbox: { width: 1.5, height: 2.75 }, aspect: 1.8 },
  'check-column': { shape: 'column', kind: 'check', aspect: 1 },
  // JEFE-4 (#133): a barrel thrown by El Chato in an arc, with the box of the barrel of EN-43 (a circle of 0.45 u).
  barrel: { shape: 'barrel', kind: 'damaging', hitbox: { width: 0.9, height: 0.9 }, aspect: 1 },
});

/**
 * JEFE-1 El Patrón (proposals, GDD §9.2): the rain falls in vertical columns from above the ceiling; phase 3 repeats
 * it faster (x 1.5) with a law helicopter; "Nápoles" throws a burst of two plush hippos every 3 s.
 */
export const JEFE_1 = Object.freeze({
  rainIntervalSeconds: 1.2,
  rainFallSpeed: 4,
  phase3SpeedFactor: 1.5,
  /** A column starts here, above the ceiling (y 12), and the share of silver coins among the rain. */
  rainSpawnY: 13,
  silverShare: 0.5,
  /** The rain falls in the part of the view ahead of the plane: from 40 % to 95 % of its width. */
  rainFromView: 0.4,
  rainToView: 0.95,
  napolesBurstSeconds: 3,
  hippoPerBurst: 2,
  hippoSpacingSeconds: 0.3,
  /** Launch of the plush hippos relative to the scroll: horizontal speed per hippo of a burst (u/s, towards the plane), the common vertical speed and the gravity of the toss. */
  hippoSpeeds: [10, 13] as readonly number[],
  hippoLaunchVy: 2,
  hippoGravity: 10,
  /** The law helicopter of phase 3 hovers at this fraction of the view width and height (u). */
  helicopterFromView: 0.25,
  helicopterY: 10,
});

/**
 * JEFE-2 El Jinete (proposals, GDD §9.2, issue #110). Horseshoes in boomerang arcs, one every 2 s, out at 8 u/s towards
 * the height of the pilot, turning inside the view (x >= 5 %) and coming back through the pilot's column. Salsa: one
 * straight horseshoe per hit of the son clave 3-2 (#109), aimed at the height of the pilot at that moment. Los
 * Caballeros: a portrait wall every 8.5 u with a gap of 3.7 u whose centre swings 1.5 u at 0.25 Hz, always at its base
 * height when the plane passes it, so consecutive gaps never differ by more than 3.5 u (§5.4).
 */
export const JEFE_2 = Object.freeze({
  horseshoeEverySeconds: 2,
  horseshoeSpeed: 8,
  /** Phase 1: the horseshoe turns when it reaches this fraction of the view width, and comes back at the same speed. */
  turnFromView: 0.05,
  /** Starting at 80 % of the view and going to 5 % takes 0.75 view widths: the vertical speed is spread over it. */
  wallSpacing: 8.5,
  wallGap: 3.7,
  wallWidth: 1.5,
  wallAmplitude: 1.5,
  wallHz: 0.25,
  /** The base height of the gap centre stays in this band, so the swing keeps it between 3.0 and 10.0. */
  wallBaseLow: 4.5,
  wallBaseHigh: 8.5,
  /** Largest step of the base between two consecutive walls (the swing is zero at the moment of passing). */
  wallMaxStep: 3.5,
  portraits: ['orejon-1', 'orejon-2', 'chepe'] as readonly string[],
});

/**
 * JEFE-3 El Padrinito (proposals, GDD §9.2, issue #121). Plazas: a board of 4 rows of 2.75 u between the ground (y 1)
 * and the ceiling (y 12), with columns of 2.75 u fixed to the world; at most 3 pieces on screen, so a board column
 * always has a free row. A piece moves one cell every second (a rook vertically, a bishop diagonally, bouncing off the
 * edges of the board), announced 0.5 s before by highlighting its destination. Llamadas: the phone rings 4 s after the
 * transition and every 5 s, and 1 s after each ring a miniboss arrives, alternating the crop duster (EN-31) and the
 * sombrero thrower (EN-32). Jaque: columns every 8 u whose gap closes linearly from 3.5 u to 3.0 u in 10 s and stays.
 */
export const JEFE_3 = Object.freeze({
  rows: 4,
  cellSize: 2.75,
  boardBottom: 1,
  maxPieces: 3,
  /** A new piece appears this often while there is room for one. */
  pieceEverySeconds: 2,
  moveEverySeconds: 1,
  warnSeconds: 0.5,
  firstRingSeconds: 4,
  ringEverySeconds: 5,
  summonDelaySeconds: 1,
  /** Where a summoned crop duster enters (u beyond the right edge), its height, and where the balcony of the sombreros stands (u from the right edge) and at what height. */
  dusterEnterBeyond: 1,
  dusterY: 6.5,
  balconyFromRight: 4,
  balconyY: 9,
  checkGapStart: 3.5,
  checkGapEnd: 3.0,
  checkCloseSeconds: 10,
  checkSpacing: 8,
  checkWidth: 1.5,
  /** The centre of the gap stays in this band and changes at most this much between two columns (<= 3.5 u, §5.4). */
  checkCentreLow: 4.5,
  checkCentreHigh: 8.5,
  checkMaxStep: 3,
});

/**
 * JEFE-4 El Chato (proposals, GDD §9.2, issue #133). He never flies: he comes out of the tunnels of the floor (y 1) and the
 * ceiling (y 12) at 80 % of the view, announced with dust 1.0 s ahead, stays out 2.5 s throwing barrels in an arc to the left
 * (the ballistics of EN-43) and hides again; the tunnel alternates from a seeded first choice. Decoys only hurt him while he
 * is out (or on the motorbike). Moto: a crossing along a horizontal tunnel at ground level at the scroll speed plus 4 u/s,
 * with the lane y 1 to 3 harmful. El Muro: two wall searchlights (EN-44) in sync and gap columns closing from 4.0 u to 3.2 u
 * in 30 s while he peeks out.
 */
export const JEFE_4 = Object.freeze({
  hiddenSeconds: 1,
  warningSeconds: 1,
  outSeconds: 2.5,
  hidingSeconds: 0.5,
  /** Ticks into an exit at which a barrel leaves: one at 0.5 s and one at 1.5 s. */
  barrelAtSeconds: [0.5, 1.5] as readonly number[],
  /** Barrels leave at this speed to the left relative to the view (u/s). */
  barrelSpeed: 5,
  /** Height of the body at the mouth of each tunnel (the box centre) and the tunnel mouth of the motorbike. */
  floorY: 2,
  ceilingY: 11,
  motoY: 2,
  /** The motorbike crosses at the scroll speed plus this (u/s) and the lane from y 1 to y 3 is harmful meanwhile. */
  motoExtraSpeed: 4,
  laneTop: 3,
  /** Phase 3: the wall searchlights hang from the ceiling at these fractions of the view; the columns every 7 u. */
  searchlightFromView: [0.4, 0.7] as readonly number[],
  searchlightY: 12,
  wallGapStart: 4.0,
  wallGapEnd: 3.2,
  wallCloseSeconds: 30,
  wallSpacing: 7,
  wallWidth: 1.5,
  wallCentreLow: 4.5,
  wallCentreHigh: 8.5,
  wallMaxStep: 2.5,
});
